import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Bell,
  Plus,
  Trash2,
  Loader2,
  Clock,
} from 'lucide-react'
import {
  useRecordatorios,
  useCrearRecordatorio,
  useEliminarRecordatorio,
} from '@/core/hooks/useTareas'
import type { TareaRecordatorio } from '@/core/types/tareas'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { format, isPast } from 'date-fns'
import { es } from 'date-fns/locale'

interface TareaRecordatoriosProps {
  tareaId: string
  className?: string
}

export function TareaRecordatorios({ tareaId, className }: TareaRecordatoriosProps) {
  const [isAdding, setIsAdding] = useState(false)
  const [fecha, setFecha] = useState('')
  const [hora, setHora] = useState('09:00')
  const [mensaje, setMensaje] = useState('')

  // Queries y mutations
  const { data: recordatorios = [], isLoading } = useRecordatorios(tareaId)
  const crearRecordatorio = useCrearRecordatorio()
  const eliminarRecordatorio = useEliminarRecordatorio()

  // Recordatorios pendientes (no enviados)
  // Mostramos todos los no enviados, ya que aunque la hora haya pasado
  // el usuario debe poder ver y gestionar sus recordatorios
  const recordatoriosPendientes = recordatorios.filter((r) => !r.enviado)

  const handleAgregar = useCallback(async () => {
    if (!fecha) {
      toast.error('Selecciona una fecha')
      return
    }

    // Crear fecha con timezone local para que Supabase la convierta correctamente a UTC
    const fechaLocal = new Date(`${fecha}T${hora}:00`)

    if (isPast(fechaLocal)) {
      toast.error('La fecha debe ser en el futuro')
      return
    }

    // Convertir a ISO string que incluye el timezone (se envía como UTC)
    const fechaRecordatorio = fechaLocal.toISOString()

    try {
      await crearRecordatorio.mutateAsync({
        tareaId,
        datos: {
          fecha_recordatorio: fechaRecordatorio,
          mensaje: mensaje.trim() || undefined,
        },
      })
      setFecha('')
      setHora('09:00')
      setMensaje('')
      setIsAdding(false)
      toast.success('Recordatorio programado')
    } catch (error) {
      console.error('Error al crear recordatorio:', error)
      toast.error('Error al programar recordatorio')
    }
  }, [fecha, hora, mensaje, tareaId, crearRecordatorio])

  const handleEliminar = useCallback(
    async (id: string) => {
      try {
        await eliminarRecordatorio.mutateAsync({ id, tareaId })
        toast.success('Recordatorio eliminado')
      } catch (error) {
        console.error('Error al eliminar recordatorio:', error)
        toast.error('Error al eliminar recordatorio')
      }
    },
    [tareaId, eliminarRecordatorio]
  )

  if (isLoading) {
    return (
      <div className={cn('flex justify-center py-2', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Bell className="h-4 w-4" />
        <h4 className="text-sm font-medium flex-1">
          Recordatorios {recordatoriosPendientes.length > 0 && `(${recordatoriosPendientes.length})`}
        </h4>
      </div>

      {/* Lista de recordatorios */}
      <div className="space-y-2 mb-3">
        {recordatoriosPendientes.map((recordatorio) => (
          <RecordatorioItem
            key={recordatorio.id}
            recordatorio={recordatorio}
            onEliminar={handleEliminar}
          />
        ))}

        {recordatoriosPendientes.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground text-center py-2">
            No hay recordatorios programados
          </p>
        )}
      </div>

      {/* Formulario para agregar */}
      {isAdding ? (
        <div className="space-y-2 p-2 border rounded-lg bg-muted/30">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Fecha</Label>
              <Input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1 h-8"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Hora</Label>
              <Input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                className="mt-1 h-8"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Mensaje (opcional)</Label>
            <Input
              placeholder="Ej: Revisar antes de enviar"
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              className="mt-1 h-8"
            />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                setFecha('')
                setHora('09:00')
                setMensaje('')
                setIsAdding(false)
              }}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleAgregar}
              disabled={!fecha || crearRecordatorio.isPending}
            >
              {crearRecordatorio.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Plus className="h-3 w-3 mr-1" />
                  Agregar
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar recordatorio
        </Button>
      )}
    </div>
  )
}

// Componente interno para un recordatorio
function RecordatorioItem({
  recordatorio,
  onEliminar,
}: {
  recordatorio: TareaRecordatorio
  onEliminar: (id: string) => void
}) {
  // new Date() convierte UTC a hora local automáticamente
  const fechaRecordatorio = new Date(recordatorio.fecha_recordatorio)

  return (
    <div className="group flex items-center gap-2 p-2 rounded-lg border bg-background hover:bg-muted/50 transition-colors">
      <Clock className="h-4 w-4 text-muted-foreground shrink-0" />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {format(fechaRecordatorio, "d 'de' MMMM, HH:mm", { locale: es })}
        </p>
        {recordatorio.mensaje && (
          <p className="text-xs text-muted-foreground truncate">
            {recordatorio.mensaje}
          </p>
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        onClick={() => onEliminar(recordatorio.id)}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  )
}
